import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { paths, type CallLog } from "@clevis/shared-types";
import { Card, CardTitle, cn } from "@clevis/shared-ui";
import { PhoneCall, PhoneIncoming, PhoneMissed, X } from "lucide-react";
import { db } from "../lib/firebase";
import { fmtPhone } from "../lib/callerPop";
import { useAuth } from "../hooks/useAuth";
import { useUiPrefs } from "../hooks/useUiPrefs";

// The caller pop. A call to one of the company's Clevis Ring numbers (or to
// the main line, forwarded to one) shows on every office screen while it
// rings and while someone's on it, with the caller matched to a customer by
// phone. Nothing to install on the phones: the phone company tells Clevis,
// Clevis tells the browser. Each person picks how it shows on their own
// screen (My profile → Incoming calls): a bar across the top, a small note in
// the corner, or not at all. Per number, the company can turn it off for
// everyone (Setup → Clevis Ring).

type Row = CallLog & { id: string };
type Phase = "ringing" | "talking" | "ended";
export type CallerPopMode = "banner" | "corner" | "off";

export const CALLER_POP_MODES: Array<{ id: CallerPopMode; label: string; help: string }> = [
  { id: "banner", label: "Across the top of the screen", help: "A bar above the page while it rings and while someone's on the call." },
  { id: "corner", label: "A small note in the corner", help: "Bottom right, out of the way of what you're doing." },
  { id: "off", label: "Don't show me calls", help: "Calls are still logged under Setup → Clevis Ring." },
];

const RING_FOR = 2 * 60_000; // a ring that never reported a pickup or an end
const TALK_FOR = 3 * 3600_000; // a call that never reported an end
const LINGER = 20_000; // the outcome stays this long after the call ends
const WINDOW = 30 * 60_000; // how far back the listener looks

function phaseOf(c: Row, now: number): Phase | null {
  if (c.endedAt) return now - c.endedAt < LINGER ? "ended" : null;
  if (c.status === "answered") return now - (c.answeredAt ?? c.startedAt) < TALK_FOR ? "talking" : null;
  if (c.status === "ringing") return now - c.startedAt < RING_FOR ? "ringing" : null;
  return null;
}
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export function IncomingCallBanner({ tenantId }: { tenantId: string }) {
  const { prefs, save } = useUiPrefs();
  const mode: CallerPopMode = prefs.callerPop ?? "banner";
  const [calls, setCalls] = useState<Row[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [now, setNow] = useState(Date.now());
  const [epoch, setEpoch] = useState(0);
  const titleBefore = useRef<string | null>(null);

  // Recent calls, live; the window slides every 10 minutes. Nothing is listened to when the person turned calls off.
  useEffect(() => {
    if (mode === "off") {
      setCalls([]);
      return;
    }
    const since = Date.now() - WINDOW;
    const unsub = onSnapshot(
      query(collection(db, paths.tenantCalls(tenantId)), where("startedAt", ">=", since), orderBy("startedAt", "desc"), limit(8)),
      (snap) => setCalls(snap.docs.map((d) => ({ id: d.id, ...(d.data() as CallLog) }))),
      () => setCalls([]),
    );
    const t = setTimeout(() => setEpoch((n) => n + 1), 10 * 60_000);
    return () => {
      unsub();
      clearTimeout(t);
    };
  }, [tenantId, epoch, mode]);

  const live = calls.filter((c) => c.pop !== false && !dismissed.has(c.id) && phaseOf(c, now));

  // A clock while anything is showing, so timers run and outcomes fade on time.
  useEffect(() => {
    if (!live.length) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [live.length]);

  // The tab title says who's calling while it rings.
  const ringing = live.find((c) => phaseOf(c, now) === "ringing");
  const ringingWho = ringing ? ringing.customerName || fmtPhone(ringing.from) || "Unknown caller" : "";
  useEffect(() => {
    if (ringingWho) {
      if (titleBefore.current === null) titleBefore.current = document.title;
      document.title = `Call: ${ringingWho}`;
    } else if (titleBefore.current !== null) {
      document.title = titleBefore.current;
      titleBefore.current = null;
    }
  }, [ringingWho]);

  if (mode === "off" || !live.length) return null;
  const corner = mode === "corner";

  const rows = live.map((c) => {
    const p = phaseOf(c, now) as Phase;
    const who = c.customerName || fmtPhone(c.from) || "Unknown caller";
    const missed = p === "ended" && c.status !== "answered";
    const secs = p === "talking" ? Math.max(0, Math.floor((now - (c.answeredAt ?? c.startedAt)) / 1000)) : c.durationSec;
    const line = p === "ringing" ? "Ringing" : p === "talking" ? `On the phone · ${mmss(secs)}` : missed ? (c.status === "busy" ? "Busy" : "Missed call") : `Answered · ${mmss(secs)}`;
    const Icon = missed ? PhoneMissed : p === "ringing" ? PhoneIncoming : PhoneCall;
    const action = "rounded bg-white/15 px-2 py-0.5 font-semibold hover:bg-white/25";
    const tone = missed ? "bg-danger" : p === "ended" ? "bg-steel" : "bg-ink";
    const open = c.customerId ? (
      <Link href={`/customers/${c.customerId}`} className={action}>
        Open customer
      </Link>
    ) : c.from ? (
      <Link href={`/customers?phone=${encodeURIComponent(fmtPhone(c.from))}`} className={action}>
        New customer
      </Link>
    ) : null;
    const quiet = (
      <>
        {!corner ? (
          <button type="button" className="opacity-70 hover:opacity-100" title="Show calls as a small note in the corner instead. Change any time under My profile." onClick={() => save({ callerPop: "corner" })}>
            Smaller
          </button>
        ) : null}
        <button type="button" className="opacity-70 hover:opacity-100" title="Stop showing calls on your screen. Turn them back on under My profile → Incoming calls." onClick={() => save({ callerPop: "off" })}>
          Off for me
        </button>
        <button type="button" aria-label="Dismiss" className="opacity-70 hover:opacity-100" onClick={() => setDismissed(new Set([...dismissed, c.id]))}>
          <X size={14} />
        </button>
      </>
    );
    return corner ? (
      <div key={c.id} className={cn("rounded-xl px-3 py-2 text-sm text-white shadow-lg", tone)}>
        <div className="flex items-center gap-2">
          <Icon size={15} className={p === "ringing" ? "animate-pulse" : undefined} />
          <span className="font-semibold">{line}</span>
          <span className="ml-auto flex items-center gap-2 text-[11px]">{quiet}</span>
        </div>
        <div className="mt-0.5 truncate">
          {who}
          {c.customerName && c.from ? <span className="opacity-80"> · {fmtPhone(c.from)}</span> : null}
          {c.source ? <span className="opacity-80"> · {c.source}</span> : null}
          {c.afterHours ? <span className="opacity-80"> · after hours</span> : null}
        </div>
        {open ? <div className="mt-1.5 text-xs">{open}</div> : null}
      </div>
    ) : (
      <div key={c.id} className={cn("flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm text-white", tone)}>
        <Icon size={16} className={p === "ringing" ? "animate-pulse" : undefined} />
        <span className="font-semibold">{line}</span>
        <span className="min-w-0 truncate">
          {who}
          {c.customerName && c.from ? <span className="opacity-80"> · {fmtPhone(c.from)}</span> : null}
          {c.source ? <span className="opacity-80"> · {c.source} number</span> : null}
          {c.afterHours ? <span className="opacity-80"> · after hours, ringing {fmtPhone(c.forwardTo)}</span> : null}
        </span>
        <span className="ml-auto flex items-center gap-3 text-xs">
          {open}
          {quiet}
        </span>
      </div>
    );
  });

  return corner ? <div className="fixed bottom-4 right-4 z-40 flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-2">{rows}</div> : <div>{rows}</div>;
}

/** My profile → Incoming calls: how this person wants calls to show. Office roles only; techs never get the pop. */
export function CallerPopPreference() {
  const { role } = useAuth();
  const { prefs, save, loaded } = useUiPrefs();
  if (role === "tech") return null;
  const mode: CallerPopMode = prefs.callerPop ?? "banner";
  return (
    <Card>
      <CardTitle>Incoming calls</CardTitle>
      <p className="mb-2 text-xs text-steel">When a call comes in on one of the company's Clevis Ring numbers, this is how it shows on your screen. Calls are logged either way.</p>
      <div className="space-y-1.5">
        {CALLER_POP_MODES.map((m) => (
          <label key={m.id} className="flex items-start gap-2 text-sm text-ink">
            <input type="radio" name="callerPop" className="mt-1" checked={mode === m.id} disabled={!loaded} onChange={() => save({ callerPop: m.id })} />
            <span>
              <span className="font-medium">{m.label}</span>
              <span className="block text-xs text-steel">{m.help}</span>
            </span>
          </label>
        ))}
      </div>
    </Card>
  );
}
